import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { T } from '@design/index';
import { WebEntryLayout } from './entryHelpers';
import { patientAuthFlow, isDemoAuthEnabled } from '../../auth/patientAuthFlow';

export default function EmailLoginScreen() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // Two-factor step-up: password accepted, server demanded an OTP.
  const [twoFactor, setTwoFactor] = useState<{ otpRequestId: string; masked?: string; demoOtp?: string } | null>(null);
  const [otpCode, setOtpCode] = useState('');

  const routeByStatus = (status: string) => {
    if (status === 'approved') {
      navigate('/patient', { replace: true });
    } else if (status === 'rejected' || status === 'need_update') {
      navigate('/patient/verification-update-required', { replace: true });
    } else {
      navigate('/patient/approval-pending', { replace: true });
    }
  };

  const handleLogin = async () => {
    setError('');
    patientAuthFlow.setLoginMethod('email');

    if (!email.trim() || !password) {
      setError('Enter your email and password.');
      return;
    }

    setBusy(true);
    const result = await patientAuthFlow.backendLogin({ email: email.trim(), password });
    setBusy(false);

    if (result.success && result.patient) {
      routeByStatus(result.approvalStatus ?? 'approved');
      return;
    }
    if (result.success && result.requiresOtp && result.otpRequestId) {
      // 2FA: show the OTP step — no token exists until the code verifies.
      setTwoFactor({ otpRequestId: result.otpRequestId, masked: result.maskedIdentifier, demoOtp: result.demoOtp });
      return;
    }
    if (result.success && result.requiresVerification) {
      navigate('/patient/login/phone', { replace: true });
      return;
    }
    if (result.error) {
      // Backend reached and rejected the credentials — show the REAL error,
      // never fall back to a local-only login on a credential failure.
      setError(result.error);
      return;
    }

    // Backend unreachable — only continue with the local-only preview flow when
    // explicitly enabled for this environment (VITE_ENABLE_DEMO_AUTH=true).
    if (!isDemoAuthEnabled()) {
      setError('Unable to reach the server. Please check your connection and try again.');
      return;
    }
    patientAuthFlow.loginVerifyOTP();
    routeByStatus(patientAuthFlow.checkApprovalAfterLogin());
  };

  const handleTwoFactorVerify = async () => {
    if (!twoFactor || otpCode.trim().length !== 6) { setError('Enter the 6-digit code.'); return; }
    setError('');
    setBusy(true);
    const verified = await patientAuthFlow.backendVerifyOtp({ otpRequestId: twoFactor.otpRequestId, code: otpCode.trim() });
    setBusy(false);
    if (verified.success && verified.patient) {
      patientAuthFlow.loginVerifyOTP();
      routeByStatus(verified.approvalStatus ?? 'approved');
      return;
    }
    setError(verified.error ?? 'Verification failed — check the code and try again.');
  };

  return (
    <WebEntryLayout>
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        padding: '48px 28px 40px',
        fontFamily: T.font,
        boxSizing: 'border-box',
        width: '100%',
      }}
    >
      <div style={{ fontSize: 22, fontWeight: 800, color: T.text, textAlign: 'center' }}>
        Email Login
      </div>
      <div style={{ fontSize: 13, color: T.textSec, textAlign: 'center', marginTop: 6 }}>
        Sign in with your email address
      </div>

      {/* Email input */}
      <div style={{ width: '100%', maxWidth: 340, marginTop: 28 }}>
        <label style={{ fontSize: 10, fontWeight: 600, color: T.textSec, marginBottom: 4, display: 'block' }}>Email Address</label>
        <input
          type="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          style={{
            width: '100%',
            padding: '12px 14px',
            border: `1px solid ${T.border}`,
            borderRadius: 12,
            fontSize: 14,
            fontFamily: T.font,
            color: T.text,
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />
      </div>

      {/* Password input */}
      <div style={{ width: '100%', maxWidth: 340, marginTop: 14 }}>
        <label style={{ fontSize: 10, fontWeight: 600, color: T.textSec, marginBottom: 4, display: 'block' }}>Password</label>
        <input
          type="password"
          placeholder="Enter your password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          style={{
            width: '100%',
            padding: '12px 14px',
            border: `1px solid ${T.border}`,
            borderRadius: 12,
            fontSize: 14,
            fontFamily: T.font,
            color: T.text,
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />
      </div>

      {/* Forgot password */}
      <div style={{ width: '100%', maxWidth: 340, marginTop: 8, textAlign: 'right' }}>
        <span onClick={() => navigate('/patient/login/forgot')} style={{ fontSize: 10, color: T.teal, fontWeight: 600, cursor: 'pointer' }}>Forgot password?</span>
      </div>

      {/* Error state (real backend errors are shown, never faked) */}
      {error && (
        <div style={{ fontSize: 12, color: T.danger, marginTop: 14, maxWidth: 340, textAlign: 'center', fontFamily: T.font }}>
          {error}
        </div>
      )}

      {/* Two-factor OTP step — password was correct; the code completes login */}
      {twoFactor && (
        <div style={{ width: '100%', maxWidth: 340, marginTop: 16, padding: 16, background: T.ice, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.text, fontFamily: T.font, marginBottom: 4 }}>Two-Factor Verification</div>
          <div style={{ fontSize: 11, color: T.textSec, fontFamily: T.font, marginBottom: 10, lineHeight: 1.5 }}>
            A 6-digit code was sent to {twoFactor.masked || 'your registered contact'}. Enter it to finish signing in.
          </div>
          {twoFactor.demoOtp && (
            <div style={{ fontSize: 10, color: '#92400E', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '6px 10px', marginBottom: 10, fontFamily: T.font }}>
              DEV code: <strong>{twoFactor.demoOtp}</strong>
            </div>
          )}
          <input
            type="text"
            inputMode="numeric"
            maxLength={6}
            placeholder="6-digit code"
            value={otpCode}
            onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
            style={{
              width: '100%', padding: '12px 14px', border: `1px solid ${T.border}`, borderRadius: 12,
              fontSize: 16, fontFamily: T.mono, letterSpacing: '0.3em', textAlign: 'center',
              color: T.text, outline: 'none', boxSizing: 'border-box',
            }}
          />
          <button
            disabled={busy || otpCode.length !== 6}
            onClick={handleTwoFactorVerify}
            style={{
              width: '100%', padding: '13px 0', marginTop: 10,
              background: otpCode.length === 6 ? `linear-gradient(135deg, ${T.navy}, ${T.overlap})` : T.border,
              color: T.white, border: 'none', borderRadius: 12, fontSize: 14, fontWeight: 700,
              fontFamily: T.font, cursor: otpCode.length === 6 ? 'pointer' : 'default',
            }}
          >
            {busy ? 'Verifying…' : 'Verify & Sign In'}
          </button>
          <button
            onClick={() => { setTwoFactor(null); setOtpCode(''); setError(''); }}
            style={{ width: '100%', padding: '8px 0', marginTop: 6, background: 'none', border: 'none', fontSize: 11, color: T.textSec, fontFamily: T.font, cursor: 'pointer' }}
          >
            Back to password
          </button>
        </div>
      )}

      {/* Login button */}
      {!twoFactor && (
      <button
        disabled={busy}
        onClick={handleLogin}
        style={{
          width: '100%',
          maxWidth: 340,
          padding: '15px 0',
          background: `linear-gradient(135deg, ${T.navy}, ${T.overlap})`,
          color: T.white,
          border: 'none',
          borderRadius: 14,
          fontSize: 15,
          fontWeight: 700,
          fontFamily: T.font,
          cursor: 'pointer',
          marginTop: 20,
          marginBottom: 16,
          boxShadow: `0 4px 14px ${T.navy}40`,
        }}
      >
        {busy ? 'Signing in…' : 'Login'}
      </button>
      )}

      {/* Switch to phone */}
      <div
        onClick={() => navigate('/patient/login/phone')}
        style={{ fontSize: 11, color: T.teal, fontWeight: 600, cursor: 'pointer', marginBottom: 10 }}
      >
        Sign in with Phone instead
      </div>

      {/* Register link */}
      <div
        onClick={() => navigate('/patient/onboarding')}
        style={{ fontSize: 13, color: T.teal, fontWeight: 600, cursor: 'pointer' }}
      >
        New? Register with NeoHealthCard
      </div>
    </div>
    </WebEntryLayout>
  );
}
